import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faArrowLeft, faComment, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { guestApi } from '../../utils/portalApi';
import { SERVER_URL } from '../../utils/api';
import Avatar from '../Avatar';
import GroupIcon from '../chat/GroupIcon';
import RichText from '../RichText';
import MediaLightbox from '../MediaLightbox';
import SharedContent from '../chat/SharedContent';

/**
 * A guest's details pane for one of their conversations (guest role): who is
 * in it, what has been shared since they could see it, and a person's card
 * with a way to message them.
 *
 * Everything comes from GET /api/portal/chats/:id/info and /shared, built from
 * the guest's own membership, so this never knows more than the server chose
 * to tell a guest: an employee's name, picture and designation; another
 * guest's name and company. Guests do not message each other, so their cards
 * have no button.
 *
 * person  { kind: 'staff' | 'guest', _id } to open straight on someone's card
 *         (a name tapped in the thread), or null for the conversation itself.
 */
const absolute = (u) => (u?.startsWith('http') ? u : `${SERVER_URL}${u}`);

const PersonCard = ({ person, kind, onBack, onClose, onMessage, opening }) => {
    const [photo, setPhoto] = useState(false);
    return (
        <div className="msgr-info guest-info">
            <div className="msgr-info-head">
                {onBack && (
                    <button type="button" className="msgr-icon-btn guest-info-back" aria-label="Back to group details" onClick={onBack}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                )}
                <button type="button" className="msgr-icon-btn guest-info-close" aria-label="Close" onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
                {person.profilePic ? (
                    <button type="button" className="guest-info-photo" aria-label={`View ${person.name}'s photo`} onClick={() => setPhoto(true)}>
                        <Avatar name={person.name} profilePic={person.profilePic} className="msgr-avatar" />
                    </button>
                ) : (
                    <Avatar name={person.name} profilePic={person.profilePic} className="msgr-avatar" />
                )}
                <h4>
                    {person.name}
                    {kind === 'guest' && <span className="ext-badge">Guest</span>}
                </h4>
                <p>{kind === 'staff' ? (person.designation || 'GTS team') : (person.company || 'Guest')}</p>
                {kind === 'staff' && person.canMessage && (
                    <div className="msgr-info-actions">
                        <button type="button" onClick={() => onMessage(person)} disabled={opening}>
                            <FontAwesomeIcon icon={opening ? faSpinner : faComment} spin={opening} /> Message
                        </button>
                    </div>
                )}
            </div>
            {photo && (
                <MediaLightbox
                    items={[{ type: 'image', url: absolute(person.profilePic), fileName: person.name }]}
                    index={0}
                    onClose={() => setPhoto(false)}
                    onIndexChange={() => { }}
                />
            )}
        </div>
    );
};

const GuestChatInfo = ({ conversation, person: initialPerson, onClose }) => {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [person, setPerson] = useState(initialPerson || null);
    const [opening, setOpening] = useState(false);
    const base = `/chats/${conversation._id}`;

    useEffect(() => { setPerson(initialPerson || null); }, [initialPerson]);

    useEffect(() => {
        let cancelled = false;
        setInfo(null);
        setError('');
        guestApi.get(`${base}/info`)
            .then(({ data }) => { if (!cancelled) setInfo(data); })
            .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'Could not load the details.'); });
        return () => { cancelled = true; };
    }, [base]);

    const message = async (who) => {
        if (who.conversationId) {
            onClose();
            navigate(`/guest/chats/${who.conversationId}`);
            return;
        }
        setOpening(true);
        try {
            const { data } = await guestApi.post('/direct', { userId: who._id });
            onClose();
            navigate(`/guest/chats/${data.conversationId}`);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not open that chat.');
        } finally {
            setOpening(false);
        }
    };

    // A direct chat is about the one person in it.
    const direct = conversation.isDirect && info?.members?.[0];
    const shown = person
        ? (person.kind === 'guest'
            ? info?.guests?.find((g) => String(g._id) === String(person._id))
            : info?.members?.find((m) => String(m._id) === String(person._id)))
        : direct;

    if (shown) {
        return (
            <PersonCard
                person={shown}
                kind={person?.kind || 'staff'}
                onBack={person && !conversation.isDirect ? () => setPerson(null) : null}
                onClose={onClose}
                onMessage={message}
                opening={opening}
            />
        );
    }

    const members = info?.members || [];
    const guests = info?.guests || [];

    return (
        <div className="msgr-info guest-info">
            <div className="msgr-info-head">
                <button type="button" className="msgr-icon-btn guest-info-close" aria-label="Close" onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
                <GroupIcon conversation={{ kind: conversation.isApp ? 'app' : 'group', name: conversation.name, avatar: conversation.avatar }} className="msgr-avatar" />
                <h4>{conversation.name}</h4>
                <p>
                    {conversation.projectName ? `${conversation.projectName} · ` : ''}
                    {info ? `${members.length + guests.length + 1} members` : 'Group'}
                </p>
                {conversation.description && <RichText text={conversation.description} style={{ marginTop: 8 }} />}
            </div>

            {error && <div className="msgr-info-section"><p className="msgr-shared-empty">{error}</p></div>}
            {!info && !error && (
                <div className="msgr-info-section"><p className="msgr-shared-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</p></div>
            )}

            {info && (
                <div className="msgr-info-section">
                    <h5>GTS team · {members.length}</h5>
                    {members.map((m) => (
                        <button type="button" key={m._id} className="msgr-member guest-member" onClick={() => setPerson({ kind: 'staff', _id: m._id })}>
                            <Avatar name={m.name} profilePic={m.profilePic} className="msgr-avatar" />
                            <span className="msgr-member-body">
                                <span className="msgr-member-name">{m.name}</span>
                                {m.designation && <span className="msgr-member-role">{m.designation}</span>}
                            </span>
                        </button>
                    ))}
                    {guests.length > 0 && <h5 className="guest-info-sub">Other guests · {guests.length}</h5>}
                    {guests.map((g) => (
                        <button type="button" key={g._id} className="msgr-member guest-member" onClick={() => setPerson({ kind: 'guest', _id: g._id })}>
                            <Avatar name={g.name} profilePic={g.profilePic} className="msgr-avatar" />
                            <span className="msgr-member-body">
                                <span className="msgr-member-name">{g.name} <span className="ext-badge">Guest</span></span>
                                {g.company && <span className="msgr-member-role">{g.company}</span>}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {info && !conversation.isApp && (
                <>
                    {info.historyFrom && (
                        <p className="guest-info-note">
                            Shared since {new Date(info.historyFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, when you were invited.
                        </p>
                    )}
                    <SharedContent key={conversation._id} conversationId={conversation._id} client={guestApi} path={`${base}/shared`} />
                </>
            )}
        </div>
    );
};

export default GuestChatInfo;
